import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContentBlockDto, ContentBlockType } from '../services/contentService';

/** Content blocks that count toward "reviewed" — inline exercises are excluded (separate journey step). */
export function isReviewableLessonBlock(block: ContentBlockDto): boolean {
  return (
    block.type !== ContentBlockType.PageSeparator &&
    block.type !== ContentBlockType.Exercise
  );
}

export function getReviewableBlockIds(pages: ContentBlockDto[][]): number[] {
  const ids: number[] = [];
  for (const page of pages) {
    for (const block of page) {
      if (isReviewableLessonBlock(block) && block.id != null) {
        ids.push(block.id);
      }
    }
  }
  return ids;
}

export function checkAllLessonContentViewed(
  pages: ContentBlockDto[][],
  viewedPages: ReadonlySet<number>,
  viewedBlocks: ReadonlySet<number>
): boolean {
  if (pages.length === 0) return false;

  if (pages.length > 1) {
    return pages.every((_, idx) => viewedPages.has(idx));
  }

  const reviewableIds = getReviewableBlockIds(pages);
  if (reviewableIds.length === 0) return true;
  return reviewableIds.every((id) => viewedBlocks.has(id));
}

export function useLessonContentViewTracking(
  lessonId: number,
  pages: ContentBlockDto[][],
  currentPage: number
) {
  const viewedPagesRef = useRef<Set<number>>(new Set());
  const viewedBlocksRef = useRef<Set<number>>(new Set());
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    viewedPagesRef.current = new Set();
    viewedBlocksRef.current = new Set();
    bump();
  }, [lessonId, bump]);

  useEffect(() => {
    if (pages.length === 0) return;
    viewedPagesRef.current.add(currentPage);
    bump();
  }, [currentPage, pages.length, bump]);

  const markBlockViewed = useCallback(
    (blockId: number) => {
      if (viewedBlocksRef.current.has(blockId)) return;
      viewedBlocksRef.current.add(blockId);
      bump();
    },
    [bump]
  );

  const allContentViewed = useMemo(
    () =>
      checkAllLessonContentViewed(
        pages,
        viewedPagesRef.current,
        viewedBlocksRef.current
      ),
    // tick drives recompute when pages/blocks are marked viewed
    [pages, tick]
  );

  const trackBlocksOnSinglePage = pages.length === 1;

  return { allContentViewed, markBlockViewed, trackBlocksOnSinglePage };
}
